// frontend/src/components/VendorTabs/BookingListView.js
import React, { useState } from 'react';
import { User, Mail, Phone, Clock, Calendar, Check, X } from 'lucide-react';
import '../../styles/BookingListView.css';

const BookingListView = ({ 
  bookings, 
  onApprove, 
  onCancel, 
  selectedService,
  isProcessing 
}) => {
  const [expandedBooking, setExpandedBooking] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    return timeString.substring(0, 5);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const isPastBooking = (date, time) => {
    const bookingDateTime = new Date(`${date}T${time}`);
    return bookingDateTime < new Date();
  };

  const filteredBookings = selectedService 
    ? bookings.filter(b => b.service_id === parseInt(selectedService))
    : bookings;

  return (
    <div className="blv-container">
      {filteredBookings.length === 0 ? (
        <div className="blv-empty">
          <Calendar className="blv-empty-icon" />
          <h3>No bookings found</h3>
          <p>
            {selectedService 
              ? 'No bookings for the selected service'
              : 'No bookings to display'}
          </p>
        </div>
      ) : (
        <div className="blv-list">
          {filteredBookings.map((booking) => {
            const isPast = isPastBooking(booking.booking_date, booking.booking_time);
            const isExpanded = expandedBooking === booking.id;

            return (
              <div 
                key={booking.id} 
                className={`blv-item ${isPast ? 'past' : ''} ${isExpanded ? 'expanded' : ''}`}
              >
                {/* Main Row */}
                <div 
                  className="blv-item-main"
                  onClick={() => setExpandedBooking(isExpanded ? null : booking.id)}
                >
                  <div className="blv-item-customer">
                    <div className="blv-avatar">
                      {booking.customer_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="blv-customer-info">
                      <h4>{booking.customer_name}</h4>
                      <span className="blv-customer-type">
                        {booking.customer_type === 'anonymous' ? '👤 New' : '✓ Registered'}
                      </span>
                    </div>
                  </div>

                  <div className="blv-item-details">
                    <div className="blv-detail">
                      <Calendar className="blv-icon" />
                      <span>{formatDate(booking.booking_date)}</span>
                    </div>
                    <div className="blv-detail">
                      <Clock className="blv-icon" />
                      <span>{formatTime(booking.booking_time)}</span>
                      {booking.end_time && (
                        <span className="blv-detail-secondary">
                          - {formatTime(booking.end_time)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="blv-item-service">
                    {booking.service_name && (
                      <span className="blv-service-badge">
                        {booking.service_name}
                      </span>
                    )}
                  </div>

                  <div className="blv-item-status">
                    <span className={`blv-status-badge ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>

                  <div className="blv-item-actions">
                    {booking.status === 'pending' && !isPast && (
                      <>
                        <button
                          className="blv-action-btn approve"
                          onClick={(e) => {
                            e.stopPropagation();
                            onApprove(booking.id);
                          }}
                          disabled={isProcessing}
                          title="Approve booking"
                        >
                          <Check className="blv-icon" />
                        </button>
                        <button
                          className="blv-action-btn cancel"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Cancel this booking?')) {
                              onCancel(booking.id);
                            }
                          }}
                          disabled={isProcessing}
                          title="Cancel booking"
                        >
                          <X className="blv-icon" />
                        </button>
                      </>
                    )}
                  </div>

                  <div className="blv-expand-arrow">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{ 
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="blv-item-expanded">
                    <div className="blv-expanded-section">
                      <h5>Contact Information</h5>
                      <div className="blv-expanded-details">
                        <div className="blv-detail">
                          <Mail className="blv-icon" />
                          <span>{booking.customer_email}</span>
                        </div>
                        {booking.customer_phone && (
                          <div className="blv-detail">
                            <Phone className="blv-icon" />
                            <span>{booking.customer_phone}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {booking.form_responses && Object.keys(booking.form_responses).length > 0 && (
                      <div className="blv-expanded-section">
                        <h5>Booking Details</h5>
                        <div className="blv-form-responses">
                          {Object.entries(booking.form_responses).map(([key, data]) => (
                            <div key={key} className="blv-response-item">
                              <span className="blv-response-label">{data.label}:</span>
                              <span className="blv-response-value">{data.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {booking.special_requests && (
                      <div className="blv-expanded-section">
                        <h5>Special Requests</h5>
                        <p className="blv-special-requests">{booking.special_requests}</p>
                      </div>
                    )}

                    <div className="blv-expanded-section">
                      <h5>Booking Information</h5>
                      <div className="blv-booking-meta">
                        <span>👥 {booking.number_of_people} {booking.number_of_people === 1 ? 'person' : 'people'}</span>
                        <span>📅 Created {new Date(booking.created_at).toLocaleDateString()}</span>
                        {booking.source_domain && (
                          <span>🌐 From {booking.source_domain}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BookingListView;